/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';
import noop from 'lodash/noop';

/**
 * Rám pro zobrazování média,
 * který provádí skrývání pomocí pozicování rámu mimo obrazovku.
 * @implements {ViewerFrame}
 */
class PositionFrame {
  /**
   * Vytvoří instanci.
   * @param {CssValue} cssValue
   * @param {StateMachineFactory} stateMachineFactory
   */
  constructor(cssValue, stateMachineFactory) {
    const el = document.createElement('div');
    el.classList.add('position-frame');
    this.cssValue = cssValue;
    this.el = el;
    this.scheduleInfo = null;
    this.stateMachine = stateMachineFactory.create({
      init: 'released',
      transitions: [
        { name: 'enter', from: 'seized', to: 'entered' },
        { name: 'leave', from: 'entered', to: 'left' },
        { name: 'release', from: ['left', 'seized'], to: 'released' },
        {
          name: 'repair',
          from: ['entered', 'left', 'released', 'seized'],
          to: 'released'
        },
        { name: 'seize', from: 'released', to: 'seized' }
      ]
    });
  }

  /** @returns {undefined} */
  enter() {
    return this.stateMachine.transit('enter', () => {
      const { scheduleInfo } = this;
      Object.assign(this.el.style, {
        left: scheduleInfo.getLeft(),
        top: scheduleInfo.getTop()
      });
    });
  }

  /** @returns {HTMLDivElement} */
  getEl() {
    return this.el;
  }

  /**
   * Skryje obalující element tak, aby nebyl na obrazovce vidět.
   * Toho se dosáhne tak, že se se element přesune mimo obsah elementu,
   * vzhledem ke kterému je pozicován.
   * Element se přesune o nejmenší nutnou vzdálenost mimo obsah,
   * na kterou je to nutné, touto vzdáleností jsou rozměry elementu.
   * Použití záporných hodnot zajistí, že se nezobrazí posuvník.
   * Spoléhá se na to, že element ke kterému se pozicuje obsah ořízne.
   * @param {ScheduleInfo} scheduleInfo
   * @private
   * @returns {undefined}
   */
  hide(scheduleInfo) {
    const width = scheduleInfo.getWidth();
    const height = scheduleInfo.getHeight();
    Object.assign(this.el.style, {
      left: this.negateCssValue(width),
      top: this.negateCssValue(height)
    });
  }

  /** @returns {undefined} */
  leave() {
    return this.stateMachine.transit('leave', () => {
      this.hide(this.scheduleInfo);
    });
  }

  /**
   * Převede CSS číslo na zápornou hodnotu.
   * Podporuje jednoduché hodnoty jako: `0`, `3px`, `50%`.
   * @param {string} string Řetězec s CSS hodnotou.
   * @private
   * @returns {string} Řetězec s CSS hodnotou se znaménkem mínus.
   */
  negateCssValue(string) {
    // TODO #295 Vyčlenit do samostatného modulu `CssUtil#negateValue()`.
    // - Vyžaduje nasazení IoC/DI pro tuto třídu.
    // TODO #295 Neumí dobře parsovat všechny čísela.
    // - https://github.com/reworkcss/css-value/issues/6
    const parsedArray = this.cssValue(string);
    if (parsedArray.length !== 1) {
      throw new Error(`Invalid CSS token count in "${string}".`);
    }
    const { type, unit, value } = parsedArray[0];
    if (type !== 'number') {
      throw new Error(`Invalid CSS token type in "${string}".`);
    }
    return `-${Math.abs(value)}${unit}`;
  }

  /** @returns {undefined} */
  release() {
    // Zatím se žádná operace neprovádí.
    // Do budoucna možno libovolně reagovat (přepozicování, zrušení hodnot v instanci, ...).
    // Skrytý element si zachovává styly dle posledního zobrazeného média.
    // Díky tomu se nemusí nic přepočítávat a ušetří se výkon.
    // Zároveň pokud bude mít následující médium stejné rozměry, tak se nemusí měnit nic.
    return this.stateMachine.transit('release', noop);
  }

  /** @returns {undefined} */
  repair() {
    return this.stateMachine.transit('repair', () => {
      const { scheduleInfo } = this;
      if (!isNil(scheduleInfo)) {
        // Skryje se element, pokud již nějaký byl přiřazen.
        this.hide(scheduleInfo);
      }
    });
  }

  /**
   * @param {ScheduleInfo} scheduleInfo
   * @returns {undefined}
   */
  seize(scheduleInfo) {
    return this.stateMachine.transit('seize', () => {
      this.scheduleInfo = scheduleInfo;
      // Zajistí se skrytí elementu, které je závislé na rozměrech média.
      // TODO #295 Zde může teoreticky na chvíli dojít k zobrazení.
      // - Za předpokladu, že byl předchozí slajd menší, tak bude zobrazen před změnou rozměrů.
      // - Pravděpodobně by se museli použít odlišné algoritmy dle rozměrů.
      // - Pozor i na z změnu z portrait do lanscape
      //   (widthA > widthB && heightA < heightB).
      // - Pozor na pořadí, ve kterém se přiřazují CSS hodnoty.
      this.hide(scheduleInfo);
      // Nastavení rozměrů a pozadí elementu, co nejdříve to jde.
      // Díky tomu se médium preloaduje ve stavu v jakém bude zobrazeno.
      // Tím se minimaliuzují nutné změny a tím i rychlost zobrazení.
      Object.assign(this.el.style, {
        backgroundColor: scheduleInfo.getBackgroundColor(),
        height: scheduleInfo.getHeight(),
        width: scheduleInfo.getWidth(),
        zIndex: scheduleInfo.getZIndex()
      });
    });
  }
}

export default PositionFrame;
